import { useEffect, useRef } from "react";

/**
 * Hook para capturar input rápido de lectores USB de código de barras.
 *
 * Los lectores USB actúan como teclado y mandan toda la cadena en <100ms terminando con Enter.
 * Si el usuario tiene el foco en cualquier parte del DOM (o ninguna), capturamos las teclas
 * globalmente cuando llegan muy rápido y disparamos onScan(barcode) al detectar Enter.
 *
 * Características:
 * - Solo captura cuando hay >= 4 caracteres recibidos en < `maxIntervalMs`
 * - Ignora input si el usuario está escribiendo en un <textarea> o input de texto largo
 *   (modal de búsqueda activo) — para no robar el teclado al usuario normal.
 * - Ignora teclas de modificadores y navegación
 *
 * Uso:
 *   useBarcodeBuffer({ enabled: true, onScan: (code) => addProductByBarcode(code) });
 */
export default function useBarcodeBuffer({
  enabled = true,
  onScan,
  maxIntervalMs = 50,
  minLength = 4,
  ignoreSelectors = ["textarea", "input[type='search']", "[data-no-scanner]"],
}) {
  const bufRef = useRef("");
  const lastTsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const reset = () => {
      bufRef.current = "";
      lastTsRef.current = 0;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const shouldIgnore = (target) => {
      if (!target || !target.closest) return false;
      // Si el foco está en un input que tiene data-scanner-target → SÍ capturar
      if (target.matches?.("[data-scanner-target]")) return false;
      for (const sel of ignoreSelectors) {
        if (target.closest(sel)) return true;
      }
      // Inputs de texto normales: solo ignorar si el usuario YA escribió algo lento ahí
      // (si está vacío y llega input rápido, también lo capturamos)
      if (target.tagName === "INPUT" && (target.type === "text" || target.type === "search")) {
        if (target.value && target.value.length > 0) return true;
      }
      return false;
    };

    const handler = (e) => {
      // Ignorar modifiers
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      // Ignorar teclas de navegación
      const ignoreKeys = ["Shift", "Control", "Alt", "Meta", "Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Escape", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12"];
      if (ignoreKeys.includes(e.key)) return;

      const now = performance.now();
      const dt = now - lastTsRef.current;

      // Si pasó mucho tiempo desde la última tecla → reiniciar buffer
      if (dt > maxIntervalMs * 6) {
        bufRef.current = "";
      }

      // Enter dispara el scan si el buffer es lo suficientemente largo y rápido
      if (e.key === "Enter") {
        const code = bufRef.current;
        if (code.length >= minLength) {
          // Heurística: si el promedio entre teclas fue rápido → probable escáner
          if (shouldIgnore(e.target)) {
            reset();
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          onScan?.(code);
        }
        reset();
        return;
      }

      // Sólo caracteres imprimibles de 1 char
      if (e.key.length !== 1) return;

      if (shouldIgnore(e.target)) {
        reset();
        return;
      }

      // Acumular
      bufRef.current += e.key;
      lastTsRef.current = now;

      // Timer de seguridad: si no llega Enter en X ms, descartar
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(reset, maxIntervalMs * 20);
    };

    window.addEventListener("keydown", handler, true);
    return () => {
      window.removeEventListener("keydown", handler, true);
      reset();
    };
  }, [enabled, onScan, maxIntervalMs, minLength, ignoreSelectors]);
}
